import * as React from 'react';

import { Button, Col, Menu, Row, Switch, TinyTable } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideEllipsis,
  LucideHash,
  LucideLayers,
  LucidePlusCircle,
  LucideShieldAlert,
  LucideShieldCheck,
  LucideSquarePen,
  LucideTrash,
} from 'lucide-react';

import { ComplianceFacilityRuleRecord } from '../../_graphql/queryComplianceFacilityRules';
import * as complianceRulesState from '../../_state/complianceRules.state';
import { useSettingsLocation } from '../../_state/settingsModule.state';
import { useTranslations } from '../../i18n/useTranslations';
import { SettingsSubmodule } from '../_layouts/SettingsSubmodule';
import { SettingsContent } from '../_shared/SettingsContent';

import { FacilityRuleFormDialog } from './FacilityRuleFormDialog';
import { JurisdictionPackMenu } from './JurisdictionPackMenu';

type RuleRowMenuProps = { row: ComplianceFacilityRuleRecord };

const RuleRowMenu = (props: RuleRowMenuProps) => {
  const t = useTranslations();
  const setEditingRule = useSetAtom(complianceRulesState.$editingFacilityRule);
  const setDialogOpen = useSetAtom(
    complianceRulesState.$isFacilityRuleDialogOpen,
  );
  const deleteFacilityRule = useSetAtom(
    complianceRulesState.$deleteFacilityRule,
  );
  const toast = useToast();

  const handleDelete = async () => {
    try {
      await deleteFacilityRule({ id: props.row.id });
      toast.success(t.complianceView.rules.dialog.messages.deleteSuccess);
    } catch (error) {
      toast.error(
        t.complianceView.rules.dialog.messages.deleteError(
          error instanceof Error ? error.message : 'Unknown error',
        ),
      );
    }
  };

  return (
    <div className="@RuleRowMenu contents">
      <Menu
        trigger={
          <Button
            className="px-2"
            size="small"
            color="neutral"
            variant="outline"
            icon={<LucideEllipsis />}
          />
        }
        items={[
          {
            label: t.complianceView.common.actions.edit,
            icon: <LucideSquarePen />,
            onAction: () => {
              setEditingRule(props.row.id);
              setDialogOpen(true);
            },
          },
          {
            label: t.complianceView.common.actions.delete,
            icon: <LucideTrash />,
            className: 'text-failure',
            secondStep: ({ close }) => (
              <Col gap3>
                <div className="text-center text-sm text-neutral/70">
                  {t.complianceView.common.confirmDelete}
                </div>
                <Row gap2>
                  <Button variant="outline" color="neutral" onPress={close}>
                    {t.complianceView.common.actions.cancel}
                  </Button>
                  <Button
                    variant="outline"
                    color="failure"
                    icon={<LucideTrash />}
                    onPress={() => {
                      close();
                      handleDelete();
                    }}
                  >
                    {t.complianceView.common.yesDelete}
                  </Button>
                </Row>
              </Col>
            ),
          },
        ]}
      />
    </div>
  );
};

type EnabledToggleProps = { row: ComplianceFacilityRuleRecord };

const EnabledToggle = ({ row }: EnabledToggleProps) => {
  const t = useTranslations();
  const [{ isPending }, updateRule] = useAtom(
    complianceRulesState.$updateFacilityRule,
  );
  const toast = useToast();

  const handleToggle = async (isEnabled: boolean) => {
    try {
      await updateRule({ id: row.id, isEnabled });
      toast.success(t.complianceView.rules.dialog.messages.updateSuccess);
    } catch (error) {
      toast.error(
        t.complianceView.rules.dialog.messages.updateError(
          error instanceof Error ? error.message : 'Unknown error',
        ),
      );
    }
  };

  return (
    <Switch
      isSelected={row.isEnabled}
      onChange={handleToggle}
      isDisabled={isPending}
    />
  );
};

const formatValue = (
  value: unknown,
  labels: { yes: string; no: string },
): string => {
  if (value === null || value === undefined) return '–';
  if (typeof value === 'boolean') return value ? labels.yes : labels.no;
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if ('value' in obj) return String(obj.value);
    return String(value);
  }
  return String(value);
};

export const FacilityRulesView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const currentRules = useAtomValue(complianceRulesState.$currentFacilityRules);
  const facilityName = useAtomValue(complianceRulesState.$currentFacilityName);
  const setDialogOpen = useSetAtom(
    complianceRulesState.$isFacilityRuleDialogOpen,
  );
  const setEditingRule = useSetAtom(complianceRulesState.$editingFacilityRule);

  const handleAddRule = React.useCallback(() => {
    setEditingRule(null);
    setDialogOpen(true);
  }, [setEditingRule, setDialogOpen]);

  return (
    <Col className="@FacilityRulesView h-full">
      <SettingsSubmodule
        title={facilityName ?? t.complianceView.rules.facilityRules.title}
        subtitle={t.complianceView.rules.facilityRules.subtitle}
        tabs={null}
        onBackPressed={() => setLocation('/compliance/rules')}
        tools={<JurisdictionPackMenu />}
      >
        <SettingsContent className="h-full">
          <Col gap4 className="h-full">
            <Row gap2>
              <Button
                className="ml-auto"
                icon={<LucidePlusCircle />}
                onPress={handleAddRule}
              >
                {t.complianceView.rules.facilityRules.actions.addRule}
              </Button>
            </Row>
            <FacilityRuleFormDialog />
            <TinyTable
              rowKey={(row) => row.id}
              isLoading={currentRules.isLoading}
              error={currentRules.error}
              columns={[
                {
                  key: 'rule',
                  label: t.complianceView.rules.facilityRules.columns.rule,
                  icon: LucideShieldCheck,
                  cell: (row) => (
                    <Col>
                      <div className="truncate font-medium">
                        {row.ruleDefinition?.name ?? row.ruleDefinitionId}
                      </div>
                      <div className="truncate text-xs text-neutral/50">
                        {row.ruleDefinition?.ruleCode}
                      </div>
                    </Col>
                  ),
                },
                {
                  key: 'category',
                  label: t.complianceView.rules.facilityRules.columns.category,
                  icon: LucideLayers,
                  width: 180,
                  cell: (row) => (
                    <div className="truncate">
                      {row.ruleDefinition?.category ?? '–'}
                    </div>
                  ),
                },
                {
                  key: 'enforcement',
                  label:
                    t.complianceView.rules.facilityRules.columns.enforcement,
                  icon: LucideShieldAlert,
                  width: 120,
                  cell: (row) => (
                    <div className="truncate">
                      {row.ruleDefinition?.enforcementLevel ?? '–'}
                    </div>
                  ),
                },
                {
                  key: 'value',
                  label: t.complianceView.rules.facilityRules.columns.value,
                  icon: LucideHash,
                  width: 120,
                  cell: (row) => (
                    <div className="truncate">
                      {formatValue(
                        row.configuredValue,
                        t.complianceView.common,
                      )}
                    </div>
                  ),
                },
                {
                  key: 'enabled',
                  label: t.complianceView.rules.facilityRules.columns.enabled,
                  width: 80,
                  preventRowClick: true,
                  cell: (row) => <EnabledToggle row={row} />,
                },
                {
                  key: 'actions',
                  label: null,
                  preventRowClick: true,
                  width: 'max-content',
                  cell: (row) => <RuleRowMenu row={row} />,
                },
              ]}
              data={currentRules.data ?? []}
            />
          </Col>
        </SettingsContent>
      </SettingsSubmodule>
    </Col>
  );
};
